import { motion } from "framer-motion";
import ScrollAnimation from "../components/animations/ScrollAnimation";
import { useTranslation } from 'react-i18next';
import { useSectionT } from "../contexts/PageVariantContext";

interface PortfolioItem {
  image: string;
  title: string;
  description: string;
  tags: string[];
}

const Portfolio = () => {
  const { t } = useTranslation();
  const sectionT = useSectionT();

  const portfolioItems: PortfolioItem[] = [
    {
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-1.2.1&auto=format&fit=crop&w=1000&q=80",
      title: t(sectionT('portfolio.items.0.title')),
      description: t(sectionT('portfolio.items.0.description')),
      tags: t(sectionT('portfolio.items.0.tags'), { returnObjects: true }) as string[]
    },
    {
      image: "https://images.unsplash.com/photo-1555421689-491a97ff2040?ixlib=rb-1.2.1&auto=format&fit=crop&w=1000&q=80",
      title: t(sectionT('portfolio.items.1.title')),
      description: t(sectionT('portfolio.items.1.description')),
      tags: t(sectionT('portfolio.items.1.tags'), { returnObjects: true }) as string[]
    },
    {
      image: "https://images.unsplash.com/photo-1551434678-e076c223a692?ixlib=rb-1.2.1&auto=format&fit=crop&w=1000&q=80",
      title: t(sectionT('portfolio.items.2.title')),
      description: t(sectionT('portfolio.items.2.description')),
      tags: t(sectionT('portfolio.items.2.tags'), { returnObjects: true }) as string[]
    }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <ScrollAnimation>
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-poppins font-bold text-primary-900 mb-4">
              {t(sectionT('portfolio.title'))}
            </h2>
            <div className="w-20 h-1 bg-accent-500 mx-auto mb-6"></div>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t(sectionT('portfolio.subtitle'))}
            </p>
          </div>
        </ScrollAnimation>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {portfolioItems.map((item, index) => (
            <ScrollAnimation key={index} delay={index * 0.2}>
              <motion.div 
                className="group relative overflow-hidden rounded-xl shadow-md"
                whileHover={{ y: -10 }}
                transition={{ duration: 0.3 }}
              >
                <div className="overflow-hidden">
                  <motion.img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-64 object-cover"
                    whileHover={{ scale: 1.1 }}
                    transition={{ duration: 0.7 }}
                  />
                </div>
                <div className="absolute bg-gray-900/50 inset-0 bg-gradient-to-t from-primary-900/90 to-transparent flex flex-col justify-end p-6">
                  <h3 className="text-xl font-poppins font-semibold text-white mb-2">
                    {item.title}
                  </h3>
                  <p className="text-gray-200 mb-4">{item.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {item.tags.map((tag, i) => (
                      <span
                        key={i}
                        className="px-3 py-1 text-white bg-accent-500/20 text-accent-300 rounded-full text-sm"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            </ScrollAnimation>
          ))}
        </div>

        <ScrollAnimation delay={0.4}>
          <div className="text-center mt-12">
            <motion.a
              href="#contact"
              className="inline-flex items-center px-6 py-3 bg-primary-900 hover:bg-primary-800 text-gray-600 font-medium rounded-lg transition-colors shadow-md hover:shadow-lg"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <span>{t(sectionT('portfolio.button'))}</span>
              <svg
                className="ml-2 w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M14 5l7 7m0 0l-7 7m7-7H3"
                ></path>
              </svg>
            </motion.a>
          </div>
        </ScrollAnimation>
      </div>
    </section>
  );
};

export default Portfolio;
